import type {
  ApplicationSubmission,
  CitDocumentRow,
  CitItemRow,
  CitOffsetLine,
  CitOffsetRow,
  CitTransferStatus,
  CustomiseItemsTransferFormData,
} from '../types/applicationForm';

/** Calendar year from `yyyy/SOC/mm/xxx` (null if not parseable). */
export function citYearFromSalesConfirmationNo(scNo: string): number | null {
  const m = String(scNo ?? '')
    .trim()
    .match(/^(\d{4})\//);
  if (!m) return null;
  const year = Number(m[1]);
  return Number.isFinite(year) ? year : null;
}

/** Prefer SC year; fall back to submitted_at year. */
export function citSubmissionListYear(row: ApplicationSubmission): number | null {
  const data = row.form_data as CustomiseItemsTransferFormData | null | undefined;
  const fromSc = citYearFromSalesConfirmationNo(data?.sales_confirmation_no ?? '');
  if (fromSc != null) return fromSc;
  const submitted = String(row.submitted_at ?? '').trim();
  if (submitted.length >= 4) {
    const year = Number(submitted.slice(0, 4));
    if (Number.isFinite(year) && year >= 2000) return year;
  }
  return null;
}

/** Newest sales confirmation first: yyyy/SOC/mm/xxx by year → month → seq. */
export function compareCitSalesConfirmationNoDesc(a: string, b: string): number {
  const parse = (raw: string) => {
    const m = raw.trim().match(/^(\d{4})\/SOC\/(\d{1,2})\/(\d+)$/i);
    if (!m) return null;
    return { year: Number(m[1]), month: Number(m[2]), seq: Number(m[3]) };
  };
  const pa = parse(a);
  const pb = parse(b);
  if (pa && pb) {
    if (pa.year !== pb.year) return pb.year - pa.year;
    if (pa.month !== pb.month) return pb.month - pa.month;
    if (pa.seq !== pb.seq) return pb.seq - pa.seq;
    return 0;
  }
  if (pa && !pb) return -1;
  if (!pa && pb) return 1;
  return b.trim().toLowerCase().localeCompare(a.trim().toLowerCase());
}

export type CitDepositStatus = {
  /**
   * Deposit arrangement is blank or No — list shows N/A.
   * Paid / Unpaid / Partial only apply when needs_deposit is Yes.
   */
  na: boolean;
  /** Fully paid: receipts present and every item’s paid qty matches SOC qty. */
  paid: boolean;
  /**
   * True when the SC is credit-cancelled (SCN/SRN only).
   * Deposit invoice exists but was reversed — not “Unpaid”.
   */
  credited: boolean;
  /** Labels for deposits that still need a real payment receipt. */
  unpaidLabels: string[];
  /** Receipts present but at least one item has paid qty below SOC qty. */
  partial: boolean;
  /** Receipts present but at least one item has paid qty above SOC qty. */
  overpaid: boolean;
};

const EMPTY_DEPOSIT_STATUS: CitDepositStatus = {
  na: false,
  paid: false,
  credited: false,
  unpaidLabels: [],
  partial: false,
  overpaid: false,
};

export type CitScItemMetrics = {
  item: CitItemRow;
  socQty: number | null;
  /** Sum of deposit invoice line quantities for this item. */
  paidQty: number;
  offsetQty: number;
  /** Remaining paid qty still available to offset: paid − offset. */
  balanceQty: number;
};

export type CitStListLine = {
  key: string;
  submissionId: string;
  stockTransferNo: string;
  itemNo: string;
  uom: string;
  offsetQty: number;
  salesConfirmationNo: string;
  customerCode: string;
  customerName: string;
  /** Stock transfer form status: Open (no form) or Completed (form created). */
  status: 'Open' | 'Completed';
  monthKey: string;
  monthLabel: string;
};

export type CitStMonthGroup = {
  monthKey: string;
  monthLabel: string;
  lines: CitStListLine[];
};

function parseQty(value: string | undefined): number | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

export function formatCitQty(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
}

/** Display invoice amounts with thousand separators and 2 decimal places (en-SG). */
export function formatCitAmount(value: string | number | null | undefined): string {
  if (value == null) return '—';
  const raw = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(raw)) {
    const text = String(value ?? '').trim();
    return text || '—';
  }
  return raw.toLocaleString('en-SG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function normalizeItemCode(value: string | undefined): string {
  return String(value ?? '').trim().toUpperCase();
}

/** Defu + Depot for one offset line (falls back to stored quantity). */
export function offsetLineTotalQty(line: CitOffsetLine): number {
  const hasDefu = Boolean(line.warehouse_defu_qty?.trim());
  const hasDepot = Boolean(line.warehouse_depot_qty?.trim());
  if (hasDefu || hasDepot) {
    const defu = parseQty(line.warehouse_defu_qty) ?? 0;
    const depot = parseQty(line.warehouse_depot_qty) ?? 0;
    return defu + depot;
  }
  return parseQty(line.quantity) ?? 0;
}

/** Sales credit / return notes — not a payment that unlocks paid qty. */
export function isCitCreditOrReturnReceiptNo(number: string): boolean {
  const key = number.trim().toUpperCase();
  return key.startsWith('SCN') || key.startsWith('SRN');
}

function filledPaymentReceiptNumbers(deposit: CitDocumentRow): string[] {
  return (deposit.payment_receipts ?? [])
    .map((receipt) => receipt.number.trim())
    .filter(Boolean);
}

/**
 * Deposit contributes to Paid qty when it has at least one payment receipt number
 * that is not SCN/SRN. SCN/SRN-only (no other receipts) → not paid.
 */
export function depositCountsTowardPaidQty(deposit: CitDocumentRow): boolean {
  const numbers = filledPaymentReceiptNumbers(deposit);
  if (numbers.length === 0) return false;
  return numbers.some((number) => !isCitCreditOrReturnReceiptNo(number));
}

/**
 * Whole entry is credit-cancelled when every filled payment receipt is SCN/SRN
 * (and at least one such receipt exists).
 */
export function isCitCreditCancelled(data: CustomiseItemsTransferFormData): boolean {
  const numbers = (data.deposit_invoices ?? []).flatMap(filledPaymentReceiptNumbers);
  if (numbers.length === 0) return false;
  return numbers.every((number) => isCitCreditOrReturnReceiptNo(number));
}

/** Sum offset qty across all offset invoices for one item (Defu + Depot). */
export function sumOffsetQtyForItem(
  data: CustomiseItemsTransferFormData,
  itemNo: string,
): number {
  const code = normalizeItemCode(itemNo);
  if (!code) return 0;
  let sum = 0;
  for (const offset of data.offset_invoices ?? []) {
    for (const line of offset.lines ?? []) {
      if (normalizeItemCode(line.item_no) !== code) continue;
      sum += offsetLineTotalQty(line);
    }
  }
  return sum;
}

/**
 * Paid qty for an item: deposit line quantities only when that deposit has a
 * real payment receipt (not SCN/SRN-only, and not missing receipt numbers).
 */
export function sumPaidQtyForItem(
  data: CustomiseItemsTransferFormData,
  itemNo: string,
): number {
  if (isCitCreditCancelled(data)) return 0;
  const code = normalizeItemCode(itemNo);
  if (!code) return 0;
  let sum = 0;
  for (const deposit of data.deposit_invoices ?? []) {
    if (!depositCountsTowardPaidQty(deposit)) continue;
    for (const line of deposit.lines ?? []) {
      if (normalizeItemCode(line.item_no) !== code) continue;
      sum += parseQty(line.quantity) ?? 0;
    }
  }
  return sum;
}

export function buildCitScItemMetrics(
  data: CustomiseItemsTransferFormData,
): CitScItemMetrics[] {
  const items = data.items?.length ? data.items : [];
  return items.map((item) => {
    const socQty = parseQty(item.sales_confirmation_qty);
    const paidQty = sumPaidQtyForItem(data, item.item_no);
    const offsetQty = sumOffsetQtyForItem(data, item.item_no);
    const balanceQty = paidQty - offsetQty;
    return { item, socQty, paidQty, offsetQty, balanceQty };
  });
}

/** Items whose total offset qty exceeds deposit paid qty. */
export function citOffsetOverPaidItems(
  data: CustomiseItemsTransferFormData,
): { itemNo: string; paidQty: number; offsetQty: number }[] {
  const over: { itemNo: string; paidQty: number; offsetQty: number }[] = [];
  const seen = new Set<string>();
  for (const item of data.items ?? []) {
    const itemNo = item.item_no.trim();
    if (!itemNo || seen.has(normalizeItemCode(itemNo))) continue;
    seen.add(normalizeItemCode(itemNo));
    const paidQty = sumPaidQtyForItem(data, itemNo);
    const offsetQty = sumOffsetQtyForItem(data, itemNo);
    if (offsetQty > paidQty + 1e-9) {
      over.push({ itemNo, paidQty, offsetQty });
    }
  }
  return over;
}

function offsetInvoiceHasContent(row: CitOffsetRow): boolean {
  return Boolean(
    row.number.trim() ||
      row.invoice_amount.trim() ||
      row.file ||
      row.stock_transfer_no.trim() ||
      row.lines.some(
        (line) =>
          line.item_no.trim() ||
          line.quantity.trim() ||
          line.warehouse_defu_qty.trim() ||
          line.warehouse_depot_qty.trim(),
      ),
  );
}

function citQtysAligned(socQty: number, paidQty: number, offsetQty: number): boolean {
  const eps = 1e-9;
  return (
    Math.abs(socQty - paidQty) <= eps &&
    Math.abs(socQty - offsetQty) <= eps &&
    Math.abs(paidQty - offsetQty) <= eps
  );
}

/**
 * Auto document status:
 * - Cancelled: only SCN/SRN payment receipts (no other receipts) — handled in withCitAutoStatus
 * - Completed: Deposit arrangement is No (no deposit / offset workflow)
 * - New: needs deposit (Yes or blank) and no offset invoice content yet
 * - Processing: at least one offset invoice started
 * - Completed (Yes): every General item with SOC qty > 0 has SOC = Paid = Offset
 *   (Balance = Paid − Offset is therefore 0). Otherwise stay Processing.
 */
export function deriveCitAutoStatus(
  data: CustomiseItemsTransferFormData,
): Exclude<CitTransferStatus, '' | 'Cancelled'> {
  if (data.needs_deposit === false) return 'Completed';

  const hasOffset = (data.offset_invoices ?? []).some(offsetInvoiceHasContent);
  if (!hasOffset) return 'New';

  const tracked = buildCitScItemMetrics(data).filter(
    (row) => row.item.item_no.trim() && row.socQty != null && row.socQty > 0,
  );
  if (
    tracked.length > 0 &&
    tracked.every(
      (row) =>
        row.socQty != null &&
        citQtysAligned(row.socQty, row.paidQty, row.offsetQty) &&
        Math.abs(row.balanceQty) <= 1e-9,
    )
  ) {
    return 'Completed';
  }
  return 'Processing';
}

/**
 * SCN/SRN-only receipts force Cancelled.
 * Otherwise keep manual Cancelled; else apply New / Processing / Completed.
 */
export function withCitAutoStatus(
  form: CustomiseItemsTransferFormData,
): CustomiseItemsTransferFormData {
  if (isCitCreditCancelled(form)) {
    return form.status === 'Cancelled' ? form : { ...form, status: 'Cancelled' };
  }
  if (form.status === 'Cancelled') return form;
  const status = deriveCitAutoStatus(form);
  return form.status === status ? form : { ...form, status };
}

/**
 * Deposit is unpaid when it has content but no real payment receipt number.
 * SCN/SRN alone does not count as Paid — but credit-cancelled SCs show Credited
 * (not Unpaid), since the deposit was reversed rather than left outstanding.
 *
 * When receipts exist, compare each item’s Paid qty to SOC qty:
 * - paid below SOC → Paid Partially
 * - paid above SOC → Overpaid
 * - all match → Paid
 */
export function citDepositStatus(data: CustomiseItemsTransferFormData): CitDepositStatus {
  if (data.needs_deposit !== true) {
    return { ...EMPTY_DEPOSIT_STATUS, na: true };
  }

  if (isCitCreditCancelled(data)) {
    return {
      ...EMPTY_DEPOSIT_STATUS,
      paid: true,
      credited: true,
    };
  }

  const unpaidLabels: string[] = [];
  const deposits = data.deposit_invoices ?? [];
  deposits.forEach((deposit, depositIndex) => {
    const hasContent = Boolean(
      deposit.number.trim() ||
        deposit.invoice_amount.trim() ||
        deposit.file ||
        deposit.lines.some((line) => line.item_no.trim() || line.quantity.trim()),
    );
    if (!hasContent) return;
    if (depositCountsTowardPaidQty(deposit)) return;
    unpaidLabels.push(
      deposit.number.trim() ||
        (deposits.length > 1 ? `Deposit ${depositIndex + 1}` : 'Deposit'),
    );
  });

  if (unpaidLabels.length > 0) {
    return {
      ...EMPTY_DEPOSIT_STATUS,
      unpaidLabels,
    };
  }

  const eps = 1e-9;
  const tracked = buildCitScItemMetrics(data).filter(
    (row) => row.item.item_no.trim() && row.socQty != null && row.socQty > 0,
  );
  let partial = false;
  let overpaid = false;
  for (const row of tracked) {
    const soc = row.socQty as number;
    if (row.paidQty + eps < soc) partial = true;
    if (row.paidQty > soc + eps) overpaid = true;
  }

  // Prefer partial when both under- and over-paid items exist on the same SC.
  if (partial) {
    return { ...EMPTY_DEPOSIT_STATUS, partial: true };
  }
  if (overpaid) {
    return { ...EMPTY_DEPOSIT_STATUS, overpaid: true };
  }

  return { ...EMPTY_DEPOSIT_STATUS, paid: true };
}

export type CitUnpaidDepositDetail = {
  key: string;
  submissionId: string;
  salesConfirmationNo: string;
  customerCode: string;
  customerName: string;
  depositNo: string;
  invoiceAmount: string;
  items: { itemNo: string; quantity: string; description: string; uom: string }[];
};

export type CitStatusDetailRow = {
  key: string;
  submissionId: string;
  salesConfirmationNo: string;
  customerCode: string;
  customerName: string;
  status: string;
  items: { itemNo: string; description: string; socQty: string; uom: string }[];
};

function itemDescription(data: CustomiseItemsTransferFormData, itemNo: string): string {
  const code = normalizeItemCode(itemNo);
  if (!code) return '';
  return (
    (data.items ?? []).find((item) => normalizeItemCode(item.item_no) === code)?.description.trim() ||
    ''
  );
}

export function listCitUnpaidDepositDetails(
  rows: ApplicationSubmission[],
): CitUnpaidDepositDetail[] {
  const out: CitUnpaidDepositDetail[] = [];
  for (const row of rows) {
    const data = row.form_data as CustomiseItemsTransferFormData;
    if (data.needs_deposit !== true) continue;
    // Credit-memo cancelled SCs are not outstanding deposits.
    if (isCitCreditCancelled(data)) continue;
    const sc = data?.sales_confirmation_no?.trim() || '—';
    const customerCode = data?.customer_code?.trim() || '';
    const customerName = data?.customer_name?.trim() || '';
    (data?.deposit_invoices ?? []).forEach((deposit, depositIndex) => {
      const hasContent = Boolean(
        deposit.number.trim() ||
          deposit.invoice_amount.trim() ||
          deposit.file ||
          deposit.lines.some((line) => line.item_no.trim() || line.quantity.trim()),
      );
      if (!hasContent) return;
      if (depositCountsTowardPaidQty(deposit)) return;
      const depositNo =
        deposit.number.trim() ||
        ((data.deposit_invoices?.length ?? 0) > 1
          ? `Deposit ${depositIndex + 1}`
          : 'Deposit');
      const items = (deposit.lines ?? [])
        .filter((line) => line.item_no.trim() || line.quantity.trim())
        .map((line) => ({
          itemNo: line.item_no.trim() || '—',
          quantity: line.quantity.trim() || '—',
          description: itemDescription(data, line.item_no),
          uom: uomForItem(data, line.item_no),
        }));
      out.push({
        key: `${row.id}:${deposit.id}`,
        submissionId: row.id,
        salesConfirmationNo: sc,
        customerCode,
        customerName,
        depositNo,
        invoiceAmount: deposit.invoice_amount.trim(),
        items,
      });
    });
  }
  return out;
}

export function listCitStatusDetails(
  rows: ApplicationSubmission[],
  status: 'New' | 'Processing' | 'Completed' | 'Cancelled' | 'all',
): CitStatusDetailRow[] {
  const out: CitStatusDetailRow[] = [];
  for (const row of rows) {
    const data = row.form_data as CustomiseItemsTransferFormData;
    const docStatus = data?.status?.trim() || '';
    if (status !== 'all' && docStatus !== status) continue;
    out.push({
      key: row.id,
      submissionId: row.id,
      salesConfirmationNo: data?.sales_confirmation_no?.trim() || '—',
      customerCode: data?.customer_code?.trim() || '',
      customerName: data?.customer_name?.trim() || '',
      status: docStatus || '—',
      items: (data?.items ?? [])
        .filter((item) => item.item_no.trim())
        .map((item) => ({
          itemNo: item.item_no.trim(),
          description: item.description.trim(),
          socQty: item.sales_confirmation_qty.trim() || '—',
          uom: item.uom.trim(),
        })),
    });
  }
  return out;
}

export function citDocumentStatusClass(status: string): string {
  const key = status.trim().toLowerCase();
  if (key === 'completed') return 'cit-status-pill cit-status-pill--completed';
  if (key === 'processing' || key === 'partial') return 'cit-status-pill cit-status-pill--processing';
  if (key === 'cancelled') return 'cit-status-pill cit-status-pill--cancelled';
  if (key === 'new' || key === 'pending' || key === 'open') {
    return 'cit-status-pill cit-status-pill--new';
  }
  return 'cit-status-pill';
}

function uomForItem(data: CustomiseItemsTransferFormData, itemNo: string): string {
  const code = normalizeItemCode(itemNo);
  if (!code) return '';
  const match = (data.items ?? []).find((item) => normalizeItemCode(item.item_no) === code);
  return match?.uom?.trim() || '';
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Parse `YYYY/STF/MM/####` into a month bucket. Unknown nos go to Other. */
export function citStockTransferMonth(stockTransferNo: string): {
  monthKey: string;
  monthLabel: string;
} {
  const match = /^(\d{4})\/STF\/(\d{2})\//i.exec(stockTransferNo.trim());
  if (match) {
    const year = match[1];
    const month = Number(match[2]);
    if (month >= 1 && month <= 12) {
      return {
        monthKey: `${year}-${match[2]}`,
        monthLabel: `${MONTH_SHORT[month - 1]} ${year}`,
      };
    }
  }
  return { monthKey: '0000-00', monthLabel: 'Other' };
}

export function buildCitStListLines(rows: ApplicationSubmission[]): CitStListLine[] {
  const lines: CitStListLine[] = [];
  for (const row of rows) {
    const data = row.form_data as CustomiseItemsTransferFormData;
    const scNo = data?.sales_confirmation_no?.trim() || '';
    const customerCode = data?.customer_code?.trim() || '';
    const customerName = data?.customer_name?.trim() || '';
    for (const offset of data?.offset_invoices ?? []) {
      const stNo = offset.stock_transfer_no?.trim() || '';
      if (!stNo) continue;
      const month = citStockTransferMonth(stNo);
      const status: CitStListLine['status'] = offset.stock_transfer_form?.path
        ? 'Completed'
        : 'Open';
      const offsetLines = offset.lines?.length ? offset.lines : [];
      if (offsetLines.length === 0) {
        lines.push({
          key: `${row.id}:${offset.id}:empty`,
          submissionId: row.id,
          stockTransferNo: stNo,
          itemNo: '',
          uom: '',
          offsetQty: 0,
          salesConfirmationNo: scNo,
          customerCode,
          customerName,
          status,
          monthKey: month.monthKey,
          monthLabel: month.monthLabel,
        });
        continue;
      }
      for (const line of offsetLines) {
        lines.push({
          key: `${row.id}:${offset.id}:${line.id}`,
          submissionId: row.id,
          stockTransferNo: stNo,
          itemNo: line.item_no.trim(),
          uom: uomForItem(data, line.item_no),
          offsetQty: offsetLineTotalQty(line),
          salesConfirmationNo: scNo,
          customerCode,
          customerName,
          status,
          monthKey: month.monthKey,
          monthLabel: month.monthLabel,
        });
      }
    }
  }
  return lines.sort((a, b) => {
    if (a.monthKey !== b.monthKey) return b.monthKey.localeCompare(a.monthKey);
    if (a.stockTransferNo !== b.stockTransferNo) {
      return b.stockTransferNo.localeCompare(a.stockTransferNo);
    }
    return a.itemNo.localeCompare(b.itemNo);
  });
}

export function groupCitStLinesByMonth(lines: CitStListLine[]): CitStMonthGroup[] {
  const map = new Map<string, CitStMonthGroup>();
  for (const line of lines) {
    const existing = map.get(line.monthKey);
    if (existing) {
      existing.lines.push(line);
      continue;
    }
    map.set(line.monthKey, {
      monthKey: line.monthKey,
      monthLabel: line.monthLabel,
      lines: [line],
    });
  }
  return [...map.values()].sort((a, b) => b.monthKey.localeCompare(a.monthKey));
}

export function citStLineSearchBlob(line: CitStListLine): string {
  return [
    line.stockTransferNo,
    line.itemNo,
    line.uom,
    formatCitQty(line.offsetQty),
    line.salesConfirmationNo,
    line.customerCode,
    line.customerName,
    line.status,
    line.monthLabel,
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n');
}

export function citScSearchBlob(row: ApplicationSubmission): string {
  const data = row.form_data as CustomiseItemsTransferFormData;
  const deposit = citDepositStatus(data);
  const items = data?.items ?? [];
  const itemParts = items.flatMap((item) => [
    item.item_no,
    item.item_no_transfer,
    item.description,
    item.sales_confirmation_qty,
    item.uom,
  ]);
  const depositNos = (data?.deposit_invoices ?? []).flatMap((doc) => [
    doc.number,
    ...(doc.payment_receipts ?? []).map((receipt) => receipt.number),
    ...(doc.lines ?? []).flatMap((line) => [line.item_no, line.quantity]),
  ]);
  const offsetParts = (data?.offset_invoices ?? []).flatMap((doc) => [
    doc.number,
    doc.stock_transfer_no,
    ...(doc.lines ?? []).flatMap((line) => [
      line.item_no,
      line.quantity,
      line.warehouse_defu_qty,
      line.warehouse_depot_qty,
    ]),
  ]);
  const clearingNos = (data?.clearing_invoices ?? []).map((doc) => doc.number);
  return [
    row.company_name,
    data?.customer_code,
    data?.customer_name,
    data?.sales_confirmation_no,
    data?.status,
    deposit.na
      ? 'n/a na deposit arrangement no'
      : deposit.credited
        ? 'credited'
        : deposit.unpaidLabels.length > 0
          ? 'unpaid'
          : deposit.partial
            ? 'paid partially awaiting balance deposit'
            : deposit.overpaid
              ? 'overpaid'
              : deposit.paid
                ? 'paid'
                : 'unpaid',
    ...deposit.unpaidLabels,
    ...itemParts,
    ...depositNos,
    ...offsetParts,
    ...clearingNos,
    row.submitted_by,
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n');
}
